import { Fragment } from 'react';

// MUI
import { faCircleMinus } from '@awesome.me/kit-379289146f/icons/classic/light';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useFieldArray } from 'react-hook-form';

// Components
import { ControlledInputs } from '@/components';

// Hooks
import { useMeetingTemplateFormContext } from '../../hooks/use-meeting-template-form-context';
const MeetingAgendaPreview = () => {
  const form = useMeetingTemplateFormContext();

  // useFieldArray for the main agenda
  const {
    fields: agendaFields,
    append: addAgendaItem,
    remove: removeAgendaItem,
  } = useFieldArray({
    control: form.control,
    name: 'agenda',
  });

  return (
    <Fragment>
      <HeaderLabels />
      {agendaFields.map((field, key) => (
        <Grid key={`agenda-${field.id}`} container spacing={3} mb={5}>
          <Grid item xs={12} sm={12} md={2} lg={2}>
            <Stack direction="row" spacing={2}>
              <IconButton
                onClick={() => removeAgendaItem(key)}
                disabled={agendaFields.length === 1}
              >
                <FontAwesomeIcon
                  icon={faCircleMinus as IconProp}
                  size="sm"
                  color={agendaFields.length === 1 ? 'grey' : 'red'}
                />
              </IconButton>
              <ControlledInputs.TextField
                ControllerProps={{
                  control: form.control,
                  name: `agenda.${key}.itemNo`,
                  defaultValue: field.itemNo || '',
                }}
                placeholder="Enter item no"
                fullWidth
                required
              />
            </Stack>
          </Grid>

          <Grid item xs={12} sm={12} md={4} lg={4}>
            <ControlledInputs.TextField
              ControllerProps={{
                control: form.control,
                name: `agenda.${key}.topic`,
                defaultValue: field.topic || '',
              }}
              placeholder="Enter topic"
              fullWidth
              required
            />
          </Grid>

          <Grid
            item
            xs={12}
            sm={12}
            md={6}
            lg={6}
            alignItems="center"
            justifyContent="center"
          >
            <DiscussionPointsFields key={field.id} agendaIndex={key} />
          </Grid>
        </Grid>
      ))}

      <Button
        variant="contained"
        color="primary"
        onClick={() => {
          const nextItemNo =
            agendaFields.length > 0
              ? `${Number(agendaFields[agendaFields.length - 1].itemNo.replace('.', '')) + 1}.`
              : '1.';

          addAgendaItem({
            itemNo: nextItemNo,
            topic: '',
            discussion_points: [{ name: '' }],
          });
        }}
        sx={{ width: '30%', textTransform: 'none' }}
      >
        Add agenda item
      </Button>
    </Fragment>
  );
};

export default MeetingAgendaPreview;

// Separate component for Discussion Points
const DiscussionPointsFields = ({ agendaIndex }: { agendaIndex: number }) => {
  const form = useMeetingTemplateFormContext();

  const {
    fields: discussionPointsFields,
    append: addDiscussionPoint,
    remove: removeDiscussionPoint,
  } = useFieldArray({
    control: form.control,
    name: `agenda.${agendaIndex}.discussion_points`,
  });

  return (
    <Stack spacing={2}>
      {discussionPointsFields.map((field, idx: number) => (
        <Stack key={field.id} direction="row" spacing={5} alignItems="center">
          <Stack direction="row" spacing={1} width={'95%'}>
            <ControlledInputs.TextField
              ControllerProps={{
                control: form.control,
                name: `agenda.${agendaIndex}.discussion_points.${idx}.name`,
                defaultValue: field.name || '',
              }}
              required
              fullWidth
              sx={{
                width: '100%',
              }}
              placeholder="Enter discussion point"
            />
            <IconButton
              onClick={() => removeDiscussionPoint(idx)}
              size="small"
              disabled={discussionPointsFields.length === 1}
            >
              <FontAwesomeIcon
                icon={faCircleMinus as IconProp}
                color={discussionPointsFields.length === 1 ? 'grey' : 'red'}
              />
            </IconButton>
          </Stack>
        </Stack>
      ))}
      <Button
        variant="contained"
        onClick={() => addDiscussionPoint({ name: '' })}
        sx={{
          marginTop: 1,
          width: '90%',
          textTransform: 'none',
        }}
      >
        Add discussion point
      </Button>
    </Stack>
  );
};

const HeaderLabels = () => (
  <Grid container textAlign="center" mb={2}>
    <Grid item xs={12} sm={12} md={2} lg={2}>
      <Typography variant="bodyBold">Item no</Typography>
    </Grid>
    <Grid item xs={12} sm={12} md={4} lg={4}>
      <Typography variant="bodyBold">Topic</Typography>
    </Grid>
    <Grid item xs={12} sm={12} md={6} lg={6}>
      <Typography variant="bodyBold">Discussion points</Typography>
    </Grid>
  </Grid>
);
